import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { ChecklistItemId, STORAGE_PREFIX } from '../constants';

// ---------------------------------------------------------------------------
// UID-namespaced localStorage helpers
// ---------------------------------------------------------------------------
function storageKey(uid: string, key: string) {
  return `${STORAGE_PREFIX}:${uid}:${key}`;
}

function readJson<T>(fullKey: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(fullKey);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(fullKey: string, value: unknown) {
  try {
    window.localStorage.setItem(fullKey, JSON.stringify(value));
  } catch {
    // storage full / blocked — silently fail
  }
}

// ---------------------------------------------------------------------------
// Tiny external-store wrapper so React re-renders on localStorage changes
// ---------------------------------------------------------------------------
let listeners: Array<() => void> = [];
let version = 0;
function subscribe(cb: () => void) {
  listeners = [...listeners, cb];
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
}
function getSnapshot() { return version; }
function emitChange() {
  version++;
  listeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useWalkthroughStore(uid: string | undefined) {
  const dismissedKey = uid ? storageKey(uid, 'dismissed') : '';
  const overridesKey = uid ? storageKey(uid, 'manualOverrides') : '';

  // Force React to re-render when we call emitChange()
  const storeVersion = useSyncExternalStore(subscribe, getSnapshot);

  const dismissed = useMemo(
    () => (uid ? readJson<boolean>(dismissedKey, false) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dismissedKey, storeVersion],
  );

  const manualOverrides = useMemo(
    () => (uid ? readJson<Partial<Record<ChecklistItemId, boolean>>>(overridesKey, {}) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overridesKey, storeVersion],
  );

  const dismiss = useCallback(() => {
    if (!uid) return;
    writeJson(dismissedKey, true);
    emitChange();
  }, [uid, dismissedKey]);

  const restore = useCallback(() => {
    if (!uid) return;
    writeJson(dismissedKey, false);
    emitChange();
  }, [uid, dismissedKey]);

  const setManualOverride = useCallback(
    (itemId: ChecklistItemId, value: boolean) => {
      if (!uid) return;
      const current = readJson<Partial<Record<ChecklistItemId, boolean>>>(overridesKey, {});
      writeJson(overridesKey, { ...current, [itemId]: value });
      emitChange();
    },
    [uid, overridesKey],
  );

  return { dismissed, manualOverrides, dismiss, restore, setManualOverride };
}
